import { useCallback, useMemo } from 'react';

import { useFeatureFlags } from 'feature-flags';

import { useConnectedClient } from 'context/ConnectedClientContext';

import {
  getActiveProduct,
  getAllowedActions,
  getAllowedRoutes,
  getDefaultRoute,
  isActionAllowed,
  isRouteAllowed,
  type ProductAction,
} from '../config/productConfig';

export const useProductAccess = () => {
  const { details } = useConnectedClient();
  const { industry_streams_ui } = useFeatureFlags(['industry_streams_ui']);

  const activeProduct = useMemo(() => getActiveProduct(details?.ConnectedTeam?.type), [details?.ConnectedTeam?.type]);

  const options = useMemo(() => ({ streamsUiEnabled: industry_streams_ui }), [industry_streams_ui]);

  const allowedRoutes = useMemo(() => getAllowedRoutes(activeProduct, options), [activeProduct, options]);
  const allowedActions = useMemo(() => getAllowedActions(activeProduct, options), [activeProduct, options]);
  const defaultRoute = useMemo(() => getDefaultRoute(activeProduct, options), [activeProduct, options]);

  const checkIsRouteAllowed = useCallback(
    (pathname: string) => isRouteAllowed(pathname, allowedRoutes),
    [allowedRoutes],
  );

  const checkIsActionAllowed = useCallback(
    (action: ProductAction) => isActionAllowed(action, allowedActions),
    [allowedActions],
  );

  return {
    activeProduct,
    allowedActions,
    allowedRoutes,
    defaultRoute,
    hasRestrictions: allowedRoutes !== null,
    isActionAllowed: checkIsActionAllowed,
    isRouteAllowed: checkIsRouteAllowed,
  };
};
